"use client"

import * as React from "react"
import { ChevronDownIcon, FileTextIcon, ImageIcon } from "lucide-react"

import { Button, buttonVariants } from "@workspace/ui/components/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu"
import {
  ImageViewer,
  type ImageViewerLabels,
} from "@workspace/ui/components/image-viewer"
import type { ReadingDirection } from "@workspace/ui/lib/lightbox-navigation"
import { cn } from "@workspace/ui/lib/utils"

export interface MenuFileLink {
  url: string
  /** Link text, and an image's alt text. */
  title: string
  type: "pdf" | "image"
}

export interface MenuFilesLabels extends ImageViewerLabels {
  /** The list's trigger when there are several files, e.g. "Menu (PDF)". */
  trigger: string
  /** Screen-reader suffix on a PDF link, e.g. "(opens in a new tab)". */
  newTab: string
}

function FileIcon({ type }: { type: MenuFileLink["type"] }) {
  return type === "image" ? (
    <ImageIcon aria-hidden />
  ) : (
    <FileTextIcon aria-hidden />
  )
}

/**
 * Entry point to a store's PDF / image menus, shown beside the online menu.
 *
 * One file is a single button labelled with its title; several sit behind a
 * "Menu (PDF)" dropdown listing each title in `files` order. A PDF opens in a
 * new tab (`target="_blank"`) — never an iframe, which phones render badly — and
 * an image opens in the zoomable {@link ImageViewer}, its title as alt text.
 *
 * Colour comes from theme tokens only. Hook for tenant CSS: `data-slot`
 * `menu-files`.
 */
function MenuFiles({
  files,
  labels,
  dir = "ltr",
  className,
}: {
  /** Never empty, in display order. */
  files: MenuFileLink[]
  labels: MenuFilesLabels
  dir?: ReadingDirection
  className?: string
}) {
  const [open, setOpen] = React.useState(false)
  // Kept after closing, so the viewer can animate out with its image.
  const [viewing, setViewing] = React.useState<MenuFileLink | null>(null)

  const view = (file: MenuFileLink) => {
    setViewing(file)
    setOpen(true)
  }

  const [only] = files
  const linkClassName = cn(
    buttonVariants({ variant: "outline" }),
    "max-w-full justify-start"
  )

  return (
    <div data-slot="menu-files" className={cn("flex", className)}>
      {files.length === 1 && only ? (
        only.type === "pdf" ? (
          <a
            href={only.url}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClassName}
          >
            <FileIcon type={only.type} />
            <span className="truncate">{only.title}</span>
            <span className="sr-only">{labels.newTab}</span>
          </a>
        ) : (
          <button
            type="button"
            onClick={() => view(only)}
            className={linkClassName}
          >
            <FileIcon type={only.type} />
            <span className="truncate">{only.title}</span>
          </button>
        )
      ) : (
        // Non-modal: a modal menu would still hold the page inert as the
        // image viewer opens from one of its items.
        <DropdownMenu dir={dir} modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <FileTextIcon aria-hidden />
              {labels.trigger}
              <ChevronDownIcon aria-hidden data-icon="inline-end" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-auto max-w-[min(20rem,calc(100vw-2rem))] min-w-(--radix-dropdown-menu-trigger-width)">
            {files.map((file, index) =>
              file.type === "pdf" ? (
                <DropdownMenuItem key={`${index}:${file.url}`} asChild>
                  <a href={file.url} target="_blank" rel="noopener noreferrer">
                    <FileIcon type={file.type} />
                    <span className="truncate">{file.title}</span>
                    <span className="sr-only">{labels.newTab}</span>
                  </a>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  key={`${index}:${file.url}`}
                  onSelect={() => view(file)}
                >
                  <FileIcon type={file.type} />
                  <span className="truncate">{file.title}</span>
                </DropdownMenuItem>
              )
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {viewing ? (
        <ImageViewer
          open={open}
          onOpenChange={setOpen}
          src={viewing.url}
          alt={viewing.title}
          title={viewing.title}
          labels={labels}
          dir={dir}
        />
      ) : null}
    </div>
  )
}

export { MenuFiles }
